// Thin wrapper around the messaging RPCs (web/Supabase auth path).
// Kiosk callers should use messaging-limited-client.ts instead.
//
// Every function returns a discriminated { data, error } so callers
// can branch without try/catch — matching the rest of the codebase.

import { supabase } from '@/integrations/supabase/client';
import type {
  AudienceKind,
  AudiencePreviewEntry,
  ConversationListEntry,
  ConversationMember,
  Message,
  MessageAttachment,
  MessageWithAttachments,
  MessageableProfile,
  SendMessageInput,
  UnreadSummaryRow,
} from '@/types/messaging';

type Result<T> = { data: T; error: null } | { data: null; error: { message: string; code?: string } };

const ok = <T>(data: T): Result<T> => ({ data, error: null });
const fail = (error: any): Result<never> => ({
  data: null,
  error: { message: error?.message ?? 'Unknown error', code: error?.code },
});

export async function listConversations(): Promise<Result<ConversationListEntry[]>> {
  const { data, error } = await supabase.rpc('list_my_conversations');
  if (error) return fail(error);
  return ok((data ?? []) as ConversationListEntry[]);
}

export async function getUnreadSummary(): Promise<Result<UnreadSummaryRow[]>> {
  const { data, error } = await supabase.rpc('get_unread_summary');
  if (error) return fail(error);
  return ok((data ?? []) as UnreadSummaryRow[]);
}

export async function listConversationMembers(conversationId: string): Promise<Result<ConversationMember[]>> {
  const { data, error } = await supabase.rpc('list_conversation_members', { p_conversation_id: conversationId });
  if (error) return fail(error);
  return ok((data ?? []) as ConversationMember[]);
}

export async function listMessageablePeople(query: string, limit = 25): Promise<Result<MessageableProfile[]>> {
  const { data, error } = await supabase.rpc('list_messageable_people', { p_query: query, p_limit: limit });
  if (error) return fail(error);
  return ok((data ?? []) as MessageableProfile[]);
}

export async function resolveAudiencePreview(
  audienceKind: AudienceKind,
  locationId: string | null,
): Promise<Result<AudiencePreviewEntry[]>> {
  const { data, error } = await supabase.rpc('resolve_audience_preview', {
    p_audience_kind: audienceKind,
    p_location_id: locationId,
  });
  if (error) return fail(error);
  return ok((data ?? []) as AudiencePreviewEntry[]);
}

export async function createDirectConversation(otherProfileId: string): Promise<Result<string>> {
  const { data, error } = await supabase.rpc('create_direct_conversation', {
    p_other_profile_id: otherProfileId,
  });
  if (error) return fail(error);
  return ok(data as string);
}

export async function createGroupConversation(name: string | null, memberIds: string[]): Promise<Result<string>> {
  const { data, error } = await supabase.rpc('create_group_conversation', {
    p_name: name,
    p_member_ids: memberIds,
  });
  if (error) return fail(error);
  return ok(data as string);
}

export async function createBroadcastConversation(input: {
  audienceKind: AudienceKind;
  audienceLocationId: string | null;
  allowReplies: boolean;
  name?: string | null;
}): Promise<Result<string>> {
  const { data, error } = await supabase.rpc('create_broadcast_conversation', {
    p_audience_kind: input.audienceKind,
    p_audience_location_id: input.audienceLocationId,
    p_allow_replies: input.allowReplies,
    p_name: input.name ?? null,
  });
  if (error) return fail(error);
  return ok(data as string);
}

export async function sendMessage(input: SendMessageInput): Promise<Result<string>> {
  const a = input.attachments ?? [];
  const { data, error } = await supabase.rpc('send_message', {
    p_conversation_id: input.conversationId,
    p_body: input.body,
    p_attachment_paths: a.map((x) => x.storagePath),
    p_attachment_mime_types: a.map((x) => x.mimeType),
    p_attachment_sizes: a.map((x) => x.sizeBytes),
    p_attachment_widths: a.length ? a.map((x) => x.width ?? null) : null,
    p_attachment_heights: a.length ? a.map((x) => x.height ?? null) : null,
    p_reply_to_id: input.replyToId ?? null,
  });
  if (error) return fail(error);
  return ok(data as string);
}

export async function editMessage(messageId: string, body: string): Promise<Result<void>> {
  const { error } = await supabase.rpc('edit_message', { p_message_id: messageId, p_body: body });
  if (error) return fail(error);
  return ok(undefined as void);
}

export async function deleteMessage(messageId: string): Promise<Result<void>> {
  const { error } = await supabase.rpc('delete_message', { p_message_id: messageId });
  if (error) return fail(error);
  return ok(undefined as void);
}

export async function markRead(conversationId: string): Promise<Result<void>> {
  const { error } = await supabase.rpc('mark_conversation_read', { p_conversation_id: conversationId });
  if (error) return fail(error);
  return ok(undefined as void);
}

export async function muteConversation(conversationId: string, until: string | null): Promise<Result<void>> {
  const { error } = await supabase.rpc('mute_conversation', {
    p_conversation_id: conversationId,
    p_until: until,
  });
  if (error) return fail(error);
  return ok(undefined as void);
}

export async function archiveConversation(conversationId: string, archived = true): Promise<Result<void>> {
  const { error } = await supabase.rpc('archive_conversation', {
    p_conversation_id: conversationId,
    p_archived: archived,
  });
  if (error) return fail(error);
  return ok(undefined as void);
}

export async function leaveConversation(conversationId: string): Promise<Result<void>> {
  const { error } = await supabase.rpc('leave_conversation', { p_conversation_id: conversationId });
  if (error) return fail(error);
  return ok(undefined as void);
}

export async function addGroupMembers(conversationId: string, profileIds: string[]): Promise<Result<void>> {
  const { error } = await supabase.rpc('add_group_members', {
    p_conversation_id: conversationId,
    p_profile_ids: profileIds,
  });
  if (error) return fail(error);
  return ok(undefined as void);
}

export async function signedAttachmentUrl(attachmentId: string): Promise<Result<string>> {
  const { data, error } = await supabase.rpc('signed_attachment_url', { p_attachment_id: attachmentId });
  if (error) return fail(error);
  return ok(data as string);
}

// Direct table reads — RLS-enforced.
//
// `reply_to:reply_to_id(...)` is a PostgREST self-embed in the FORWARD
// direction: "the row whose id is THIS row's reply_to_id". The earlier
// `reply_to:messages!reply_to_id(...)` form resolved as REVERSE
// ("other messages whose reply_to_id points at me"), which returned
// an empty array on every actual reply (and the wrong row on the few
// targets that had been replied to) — Brian saw replies render with
// no quote because of this. The forward syntax returns a single
// object or null, no array wrapping.
const MESSAGE_COLUMNS =
  'id, conversation_id, sender_id, body, created_at, edited_at, deleted_at, deleted_by, reply_to_id, ' +
  'message_attachments(*), ' +
  'message_reactions(id, message_id, profile_id, emoji, created_at), ' +
  'message_read_receipts(message_id, profile_id, read_at), ' +
  'reply_to:reply_to_id(id, sender_id, body, deleted_at)';

const REPLY_PREVIEW_MAX = 140;

const mapMessageRow = (m: any): MessageWithAttachments => {
  // PostgREST's self-embed for `reply_to:messages!reply_to_id(...)` is
  // returned as an ARRAY (empty `[]` when reply_to_id is NULL), not a
  // single object — supabase-js doesn't auto-collapse self-joins into
  // a one-to-one. An empty array is truthy in JS, so a naive
  // `replyRaw ? {...} : null` produced a ReplyQuote on every message
  // with sender_id/body undefined → the dreaded "Someone / (empty
  // message)" phantom quotes that appeared on every bubble after
  // 20260603120000 deployed. Normalise here.
  const rawReply = m.reply_to;
  const replyRow: { id?: string; sender_id?: string; body?: string | null; deleted_at?: string | null } | null =
    Array.isArray(rawReply) ? (rawReply[0] ?? null) : (rawReply ?? null);
  const reply_to = replyRow && replyRow.id
    ? {
        id: replyRow.id,
        sender_id: replyRow.sender_id ?? '',
        body: (replyRow.body ?? '').slice(0, REPLY_PREVIEW_MAX),
        deleted_at: replyRow.deleted_at ?? null,
      }
    : null;
  return {
    id: m.id,
    conversation_id: m.conversation_id,
    sender_id: m.sender_id,
    body: m.body,
    created_at: m.created_at,
    edited_at: m.edited_at,
    deleted_at: m.deleted_at,
    deleted_by: m.deleted_by ?? null,
    reply_to_id: m.reply_to_id ?? null,
    attachments: ((m.message_attachments ?? []) as MessageAttachment[]).sort(
      (a, b) => a.created_at.localeCompare(b.created_at),
    ),
    reactions: (m.message_reactions ?? []) as MessageWithAttachments['reactions'],
    read_receipts: (m.message_read_receipts ?? []) as MessageWithAttachments['read_receipts'],
    reply_to,
  };
};

export async function loadMessagesPage(
  conversationId: string,
  opts: { before?: string; limit?: number } = {},
): Promise<Result<MessageWithAttachments[]>> {
  const limit = opts.limit ?? 50;
  let q = supabase
    .from('messages')
    .select(MESSAGE_COLUMNS)
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (opts.before) {
    q = q.lt('created_at', opts.before);
  }
  const { data, error } = await q;
  if (error) return fail(error);
  return ok((data ?? []).map(mapMessageRow));
}

export async function loadMessageWithAttachments(messageId: string): Promise<Result<MessageWithAttachments | null>> {
  const { data, error } = await supabase
    .from('messages')
    .select(MESSAGE_COLUMNS)
    .eq('id', messageId)
    .maybeSingle();
  if (error) return fail(error);
  if (!data) return ok(null);
  return ok(mapMessageRow(data));
}

export async function toggleReaction(messageId: string, emoji: string): Promise<Result<boolean>> {
  const { data, error } = await supabase.rpc('toggle_message_reaction', {
    p_message_id: messageId,
    p_emoji: emoji,
  });
  if (error) return fail(error);
  return ok(data as boolean);
}

export type { Result };
